import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams, useNavigate } from "react-router-dom";
import { API } from "@/contexts/AppContext";
import { toast } from "sonner";
import { CheckCircle2, CreditCard, Smartphone, Zap, Crown, Rocket, Lock, Loader2, Gift } from "lucide-react";

const PLAN_META = {
  trial: { icon: Gift, color: "#7A8471", accent: "#7A8471", tag: "Gratuit 30 jours" },
  basic: { icon: Zap, color: "#1E4631", accent: "#E5EBE7", tag: "Démarrage" },
  pro: { icon: Rocket, color: "#D96C4A", accent: "#D96C4A", tag: "Le plus populaire" },
  premium: { icon: Crown, color: "#E3A72F", accent: "#E3A72F", tag: "Pour les grands hôpitaux" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return iso; }
};

export default function Subscribe() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState(null);
  const [selected, setSelected] = useState("pro");
  const [currency, setCurrency] = useState("USD");
  const [phone, setPhone] = useState("243");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/subscriptions/plans`).then((r) => setPlans(r.data.plans || [])).catch(() => {});
    axios.get(`${API}/subscriptions/me`).then((r) => setCurrent(r.data.subscription)).catch(() => {});
  }, []);

  useEffect(() => {
    // Handle return from PawaPay checkout
    const cid = params.get("checkoutId");
    if (cid) {
      const mock = params.get("mock") === "1";
      if (mock) {
        toast.success("Paiement simulé validé — abonnement activé (mode sandbox)");
      } else {
        axios.get(`${API}/payments/${cid}`).then((r) => {
          if (r.data.status === "COMPLETED") toast.success("Paiement confirmé, abonnement activé !");
          else toast.info(`Statut paiement : ${r.data.status}`);
        }).catch(() => {});
      }
      // refresh subscription
      axios.get(`${API}/subscriptions/me`).then((r) => setCurrent(r.data.subscription)).catch(() => {});
    }
  }, [params]);

  const handlePay = async () => {
    if (!phone || phone.length < 12) {
      toast.error("Numéro invalide : format 243XXXXXXXXX (12 chiffres)");
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/subscriptions/checkout`, {
        plan_id: selected,
        phone_number: phone,
        currency,
      });
      if (r.data.redirectUrl && !r.data.mock) {
        window.location.href = r.data.redirectUrl;
      } else if (r.data.mock) {
        toast.success("Paiement Mobile Money simulé avec succès (mode sandbox — sans clé PawaPay)");
        const sub = await axios.get(`${API}/subscriptions/me`);
        setCurrent(sub.data.subscription);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec du paiement");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8" data-testid="subscribe-page">
      <div>
        <h1 className="text-3xl font-[Outfit] font-bold text-[#1A1D1A] flex items-center gap-3">
          <CreditCard className="w-7 h-7 text-[#1E4631]"/> Abonnement KWIKILA Care
        </h1>
        <p className="text-[#5C6B61] mt-1 text-sm">
          Payez par Mobile Money — Airtel Money, Orange Money ou Vodacom M-Pesa. Renouvelable chaque mois.
        </p>
      </div>

      {current && current.status === "active" && (
        <div className="bg-[#2E7D32]/10 border border-[#2E7D32]/30 rounded-2xl p-5 flex items-start gap-4" data-testid="current-sub-banner">
          <CheckCircle2 className="w-6 h-6 text-[#2E7D32] flex-shrink-0 mt-0.5"/>
          <div className="flex-1">
            <div className="font-semibold text-[#1A1D1A]">
              Abonnement actif — Plan {current.plan_name}
            </div>
            <div className="text-sm text-[#5C6B61] mt-0.5">
              Valide jusqu&apos;au <b>{fmtDate(current.current_period_end)}</b> · {current.currency} {current.amount} / mois
            </div>
          </div>
        </div>
      )}

      {/* Plan cards */}
      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5">
        {plans.map((p) => {
          const meta = PLAN_META[p.id] || PLAN_META.basic;
          const Icon = meta.icon;
          const isTrial = p.id === "trial";
          const isSel = selected === p.id;
          const isCurrent = current?.plan_id === p.id && current?.status === "active";
          return (
            <button
              key={p.id}
              onClick={() => !isTrial && setSelected(p.id)}
              data-testid={`plan-card-${p.id}`}
              className={`relative text-left rounded-2xl p-6 border-2 transition-all bg-white ${
                isSel ? "shadow-lg scale-[1.02]" : "hover:shadow-md"
              } ${isTrial ? "cursor-default" : ""}`}
              style={{ borderColor: isSel ? meta.color : "#E5EBE7" }}
            >
              {(p.id === "pro" || isTrial) && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full" style={{ background: meta.color }}>
                  {meta.tag}
                </div>
              )}
              {isCurrent && (
                <div className="absolute top-3 right-3 bg-[#2E7D32]/10 text-[#2E7D32] text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded">
                  Actif
                </div>
              )}
              <div className="w-11 h-11 rounded-xl grid place-items-center" style={{ background: `${meta.color}15`, color: meta.color }}>
                <Icon className="w-6 h-6"/>
              </div>
              <div className="mt-3 font-[Outfit] font-bold text-2xl">{p.name}</div>
              <div className="mt-1 text-3xl font-[Outfit] font-bold" style={{ color: meta.color }}>
                {isTrial ? "0$" : `$${p.price_usd}`}<span className="text-sm font-normal text-[#5C6B61]"> {isTrial ? "/ 30 jours" : "/mois"}</span>
              </div>
              <div className="text-xs text-[#5C6B61]">{isTrial ? "Inclus à l'inscription" : `≈ ${p.price_cdf.toLocaleString("fr-FR")} CDF`}</div>
              <ul className="mt-4 space-y-2 text-sm">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: meta.color }}/>
                    <span className="text-[#1A1D1A]">{f}</span>
                  </li>
                ))}
              </ul>
            </button>
          );
        })}
      </div>

      {/* Payment form */}
      <div className="bg-white rounded-2xl border border-[#E5EBE7] p-6" data-testid="payment-form">
        <h2 className="font-[Outfit] font-semibold text-lg flex items-center gap-2">
          <Smartphone className="w-5 h-5 text-[#1E4631]"/> Payer par Mobile Money
        </h2>
        <p className="text-xs text-[#5C6B61] mt-1">
          Le système détecte automatiquement l&apos;opérateur (Airtel, Orange, Vodacom) à partir de votre numéro.
        </p>

        <div className="grid md:grid-cols-3 gap-4 mt-5">
          <div>
            <label className="text-xs font-semibold text-[#5C6B61] uppercase tracking-wide">Plan choisi</label>
            <div className="mt-1 h-11 px-3 rounded-lg border border-[#E5EBE7] bg-[#FDFBF7] flex items-center font-semibold text-[#1A1D1A]" data-testid="selected-plan-label">
              {plans.find((x) => x.id === selected)?.name || "—"}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-[#5C6B61] uppercase tracking-wide">Devise</label>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              data-testid="currency-select"
              className="mt-1 w-full h-11 px-3 rounded-lg border border-[#E5EBE7] bg-white text-sm outline-none focus:border-[#1E4631]"
            >
              <option value="USD">USD (dollar)</option>
              <option value="CDF">CDF (franc congolais)</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-[#5C6B61] uppercase tracking-wide">Numéro Mobile Money</label>
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/[^\d]/g, ""))}
              placeholder="243812345678"
              maxLength={12}
              data-testid="phone-input"
              className="mt-1 w-full h-11 px-3 rounded-lg border border-[#E5EBE7] bg-white text-sm outline-none focus:border-[#1E4631]"
            />
            <div className="text-[11px] text-[#5C6B61] mt-1">Format : 243 + 9 chiffres</div>
          </div>
        </div>

        <button
          onClick={handlePay}
          disabled={loading}
          data-testid="pay-button"
          className="mt-6 w-full md:w-auto inline-flex items-center justify-center gap-2 px-8 py-3 rounded-xl bg-[#1E4631] hover:bg-[#153224] text-white font-semibold transition disabled:opacity-60"
        >
          {loading ? <><Loader2 className="w-4 h-4 animate-spin"/> Traitement…</> :
                     <><Lock className="w-4 h-4"/> Payer maintenant</>}
        </button>

        <div className="mt-4 text-xs text-[#5C6B61] flex items-start gap-2">
          <Lock className="w-3 h-3 mt-0.5 flex-shrink-0"/>
          <span>Paiement sécurisé via PawaPay. Aucune donnée bancaire n&apos;est stockée sur nos serveurs.</span>
        </div>
      </div>

      <button
        onClick={() => navigate("/dashboard")}
        className="text-sm text-[#5C6B61] hover:text-[#1E4631] underline"
        data-testid="back-to-dashboard"
      >
        ← Retour au tableau de bord
      </button>
    </div>
  );
}
